import { tr } from "../i18n/LanguageContext";
import React, { useEffect, useRef, useState } from "react";
import { apiErrorMessage } from "../lib/apiError";
import { useSearchParams, useNavigate as useRawNavigate } from "react-router-dom";
import { useNavigate } from "../i18n/router";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const MagicLinkCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawNavigate = useRawNavigate();
  const { checkAuthStatus } = useAuth();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const redeemedRef = useRef(false);

  useEffect(() => {
    if (redeemedRef.current) return;
    redeemedRef.current = true;
    const handleMagicLinkCallback = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setMessage(tr("Недействительная ссылка для входа"));
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        // Read stored redirect BEFORE clearing anything
        const storedRedirect = localStorage.getItem('loginRedirect');

        const response = await fetch(
          `${backendUrl}/api/auth/callback?token=${token}`,
          { method: "GET", credentials: "include" }
        );

        const data = await response.json();

        if (response.ok) {
          setStatus("success");
          setMessage(data.message || tr("Успешный вход в систему"));

          // Clear ALL stale client state
          localStorage.removeItem('loginRedirect');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('session');
          sessionStorage.clear();

          // Refresh auth context so navbar updates
          await checkAuthStatus();

          // Append a welcome flag for the home page banner. If a deeper redirect
          // is requested (e.g. /add-event), respect that — the banner is only for /.
          const isHomeRedirect = !storedRedirect || !storedRedirect.startsWith('/') || storedRedirect === '/' || storedRedirect === '/en';
          const target = isHomeRedirect ? `${storedRedirect === '/en' ? '/en' : '/'}?welcome=1` : storedRedirect;

          setTimeout(() => {
            rawNavigate(target, { replace: true });
          }, 1200);
        } else {
          setStatus("error");
          setMessage(apiErrorMessage(data, tr("Ошибка при входе в систему")));
        }
      } catch (error) {
        console.error("[MagicLinkCallback] Error:", error);
        setStatus("error");
        setMessage(tr("Произошла неожиданная ошибка"));
      }
    };

    handleMagicLinkCallback();
  }, [searchParams, navigate, checkAuthStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full text-center">
        <div className="eunoia-surface p-10">
          <div className="flex justify-center mb-6">
            {status === "loading" && (
              <Loader2 className="h-12 w-12 animate-spin" style={{color: 'var(--eunoia-accent)'}} />
            )}
            {status === "success" && (
              <CheckCircle className="h-12 w-12" style={{color: 'var(--eunoia-accent)'}} />
            )}
            {status === "error" && (
              <XCircle className="h-12 w-12 text-red-500" />
            )}
          </div>

          <h1 className="eunoia-title-medium mb-4">
            {status === "loading" && tr("Проверяем ссылку...")}
            {status === "success" && tr("Добро пожаловать!")}
            {status === "error" && tr("Ошибка входа")}
          </h1>

          <p className={`eunoia-body mb-6 ${status === "error" ? "text-red-600" : ""}`}>
            {status === "loading" && tr("Пожалуйста, подождите")}
            {status === "success" && tr("Вы успешно вошли в систему")}
            {status === "error" && message}
          </p>

          {status === "success" && (
            <div className="w-full rounded-full h-1 mb-4" style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
              <div className="h-1 rounded-full animate-pulse" style={{width: '100%', backgroundColor: 'var(--eunoia-accent)'}} />
            </div>
          )}

          {status === "error" && (
            <div className="space-y-3">
              <button onClick={() => navigate("/login")} className="eunoia-btn-primary w-full">
                {tr("Попробовать снова")}
              </button>
              <button onClick={() => navigate("/")} className="eunoia-btn-secondary w-full">
                {tr("На главную")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MagicLinkCallback;
